import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_URL) || '';
const supabasePublishableKey = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY) || '';

if (!supabaseUrl || !supabasePublishableKey) {
  console.warn('Supabase URL or Publishable Key missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in .env');
}

/**
 * Singleton Supabase client.
 * Uses the publishable key (formerly "anon key") — Row-Level Security enforces authorization.
 * Auto-converts snake_case column names to camelCase for JS.
 */
export const supabase: SupabaseClient = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabasePublishableKey || 'placeholder',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
    },
    global: {
      // FIGYELEM: ez a fejléc MINDEN kérésre rákerül, a `functions.invoke`
      // hívásokra is. Ezért minden böngészőből hívott Edge Function
      // `Access-Control-Allow-Headers` listájában szerepelnie kell —
      // különben a böngésző a preflightnál blokkol, és a hiba
      // ("Failed to send a request to the Edge Function") semmit nem árul
      // el az okról. Ha ide új fejléc kerül, a függvényeket is bővítsd.
      headers: { 'x-rehome-client': 'platform' },
    },
  }
);

/** Check if Supabase is configured with real credentials. */
export const isSupabaseConfigured = (): boolean => Boolean(supabaseUrl && supabasePublishableKey);

/**
 * Helper: execute a Supabase query with graceful error handling.
 * Returns { data, error } — caller decides how to handle errors.
 */
export const query = async <T>(
  tableName: string,
  builder: (q: ReturnType<typeof supabase.from>) => any
): Promise<{ data: T[] | null; error: string | null }> => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: 'Supabase not configured' };
  }
  try {
    const { data, error } = await builder(supabase.from(tableName));
    if (error) throw error;
    return { data: data as T[], error: null };
  } catch (err: any) {
    console.error(`Supabase query error on ${tableName}:`, err);
    return { data: null, error: err.message || 'Unknown error' };
  }
};
